import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Sparkles, Target, Timer, TrendingUp, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { previewScreens, pickLang, type PreviewScreen, type Lang } from "@/data/previewScreens";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface Props {
  /** When true, renders a tight 2-column grid (for /preview page). Otherwise a single column scroll for Landing teaser. */
  dense?: boolean;
  /** Limit number of screens (used for a Landing teaser). */
  limit?: number;
  /** Explicit ordered list of screen ids to show. Overrides `limit`. */
  ids?: string[];
  /** Heading level for screen titles. */
  headingTag?: "h2" | "h3";
  /** Append an AI insights teaser card as the final grid cell. */
  appendAiTeaser?: boolean;
  /** Presentation mode: responsive grid (default) or one-card-at-a-time carousel. */
  mode?: "grid" | "carousel";
}

/** Ordered ids used by the homepage "What 25 matches can reveal" section. */
export const SHOWCASE_IDS = [
  "stats-tiles",
  "win-rate-trend",
  "score-per-set",
  "performance-report",
  "smartwatch-stats",
];

function ScreenshotCard({
  screen,
  lang,
  HeadingTag,
}: {
  screen: PreviewScreen;
  lang: Lang;
  HeadingTag: "h2" | "h3";
}) {
  return (
    <Card className="h-full overflow-hidden bg-gradient-card border-border/60 shadow-card">
      <div className="bg-secondary/30 border-b border-border/60 p-2">
        <img src={screen.src} alt={screen.alt[lang]} loading="lazy" decoding="async" className="w-full h-auto rounded-md" />
      </div>
      <div className="p-4 sm:p-5 space-y-1.5">
        <HeadingTag className="font-display text-lg sm:text-xl tracking-wide text-foreground">
          {screen.title[lang]}
        </HeadingTag>
        <p className="text-sm text-muted-foreground leading-relaxed">{screen.caption[lang]}</p>
      </div>
    </Card>
  );
}

function AiTeaserCard({ HeadingTag, isEl, title }: { HeadingTag: "h2" | "h3"; isEl: boolean; title: string }) {
  return (
    <Card className="h-full overflow-hidden border-primary/40 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent shadow-glow">
      <div className="p-5 sm:p-6 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow">
            <Sparkles className="w-5 h-5 text-primary-foreground" />
          </div>
          <HeadingTag className="font-display text-lg sm:text-xl tracking-wide text-foreground">{title}</HeadingTag>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {isEl
            ? "Η ανάλυση AI είναι διαθέσιμη μόλις καταγράψετε δεδομένα αγώνα και γενικά γίνεται πιο ενημερωτική όσο μεγαλώνει το ιστορικό των αγώνων σας."
            : "AI analysis is available once you have logged match data and generally becomes more informative as your match history grows. It uses privacy-filtered, allowlisted match statistics, ratings and selected context to surface patterns worth reviewing."}
        </p>

        <div className="rounded-lg border border-primary/20 bg-card/50 p-3.5 sm:p-4 space-y-3.5 mb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-win">
              <Target className="w-3.5 h-3.5" />
              <span>{isEl ? "Μοτίβο αγώνα" : "Match pattern"}</span>
            </div>
            <p className="text-sm text-foreground/90 leading-snug">
              {isEl
                ? "«Κερδίζεις 78% των αγώνων όταν παίρνεις το 1ο σετ.»"
                : "“You win 78% of matches when you take the first set."}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-loss">
              <Timer className="w-3.5 h-3.5" />
              <span>{isEl ? "Φυσική κατάσταση" : "Fitness"}</span>
            </div>
            <p className="text-sm text-foreground/90 leading-snug">
              {isEl
                ? "«Το win rate πέφτει σε αγώνες πάνω από 90 λεπτά.»"
                : "“Your win rate drops in matches longer than 90 minutes."}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>{isEl ? "Βελτίωση" : "Improvement"}</span>
            </div>
            <p className="text-sm text-foreground/90 leading-snug">
              {isEl
                ? "«Το backhand rating σου βελτιώθηκε στους τελευταίους 5 αγώνες.»"
                : "“Your backhand rating has improved across your last 5 matches."}
            </p>
          </div>
        </div>

        <Link
          to="/demo"
          className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {isEl ? "Εξερεύνησε AI insights" : "Explore AI insights"}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Card>
  );
}

export default function StatsPreviewShowcase({
  dense = false,
  limit,
  ids,
  headingTag = "h3",
  appendAiTeaser = false,
  mode = "grid",
}: Props) {
  const { i18n, t } = useTranslation();
  const lang = pickLang(i18n.language);
  const items = ids
    ? ids.map((id) => previewScreens.find((s) => s.id === id)).filter((s): s is PreviewScreen => Boolean(s))
    : typeof limit === "number"
      ? previewScreens.slice(0, limit)
      : previewScreens;
  const HeadingTag = headingTag;
  const isEl = i18n.language.startsWith("el");
  const aiTitle = t("landing.aiTeaserTitle", {
    defaultValue: isEl ? "AI Insights — ο προσωπικός σου coach" : "AI Insights — your personal coach",
  });

  const cells: { key: string; node: ReactNode }[] = items.map((s) => ({
    key: s.id,
    node: <ScreenshotCard screen={s} lang={lang} HeadingTag={HeadingTag} />,
  }));
  if (appendAiTeaser) {
    cells.push({ key: "ai-teaser", node: <AiTeaserCard HeadingTag={HeadingTag} isEl={isEl} title={aiTitle} /> });
  }

  if (mode === "carousel") {
    return <ShowcaseCarousel cells={cells} />;
  }

  return (
    <div className={dense ? "grid gap-6 md:grid-cols-2" : "grid gap-6 sm:grid-cols-2"}>
      {cells.map((c) => (
        <div key={c.key} className="h-full">
          {c.node}
        </div>
      ))}
    </div>
  );
}

function ShowcaseCarousel({ cells }: { cells: { key: string; node: ReactNode }[] }) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <div className="relative">
      <Carousel setApi={setApi} opts={{ align: "start", loop: false }} className="w-full">
        <CarouselContent>
          {cells.map((c) => (
            <CarouselItem key={c.key} className="basis-full">
              {c.node}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-4 flex items-center justify-center gap-3" role="tablist" aria-label="Showcase slides">
        <button
          type="button"
          onClick={() => api?.scrollPrev()}
          disabled={current === 0}
          aria-label="Previous slide"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-card text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {cells.map((c, i) => (
          <button
            key={c.key}
            type="button"
            role="tab"
            aria-selected={i === current}
            aria-label={`Slide ${i + 1}`}
            onClick={() => api?.scrollTo(i)}
            className={cn(
              "h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              i === current ? "w-6 bg-primary" : "w-2.5 bg-border"
            )}
          />
        ))}
        <button
          type="button"
          onClick={() => api?.scrollNext()}
          disabled={current === cells.length - 1}
          aria-label="Next slide"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-card text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
